import { useEffect, useRef, useState } from 'react'
import { Outlet, useRouterState, useNavigate } from '@tanstack/react-router'
import { CONNECTED_APPS_PATH, CONSENT_PATH } from '../oauth/config'
import { cx, Modal, useMessage } from '@solum/ui'
import { Topbar } from './Topbar'
import { Nav } from './Nav'
import { CommandPalette } from './CommandPalette'
import { ActivityComposerHost } from './ActivityComposerHost'
import { NavEditMode } from './NavEditMode'
import { CrmSettingsSync } from './providerSettingsSync'
import { useRecentsStore } from './recents'
import { ShellDrawer } from './ShellDrawer'
import { ConsoleNavigationShell } from './ConsoleNavigationShell'
import { setDrawerContextFromRoute, bindingForRoute, declareContext } from '@solum/ui'
import { onPaletteOpen, usePaletteScope } from './paletteScope'
import { ctxFor } from '../copilot/brain'
import { launchCopilot } from '../copilot/launcher'
import { useAuth } from '@solum/auth'
import { useSolum } from '../data/client'
import { useMyAreas } from '../access/access'
import { registry } from '../data/registry'
import { MOBILE_QUERY } from './breakpoints'
import { UnsavedChangesGuard } from './UnsavedChangesGuard'
import { isStudioPath } from '../settings/pages'
import { paletteOwnerFor } from './palette/owner'
import { resetPersonaForPrincipal } from './persona'
import s from './AppShell.module.css'

// A customer-only identity may reach only their own area — home, My Organization,
// and a shared record. Everything else is backstage. Nav hides the links, but a
// DEEP-LINK must not land a customer on a backstage page (defense in depth on top
// of DB RLS, and #37 role-area gating). Anything else → bounce to /my-org.
function customerAllowed(pathname: string): boolean {
  return pathname === '/' || pathname.startsWith('/my-org') || pathname.startsWith('/share')
    // #2443: the OAuth consent page. It shows a customer-only principal an
    // explicit refusal and lets them Deny — which ends GoTrue's transaction —
    // instead of bouncing away and leaving it pending. It renders nothing
    // backstage, and only a Console (backstage) user is ever offered Allow.
    || pathname === CONSENT_PATH || pathname === '/auth/assurance'
}

/* ── THE COLLAPSED RAIL IS REMEMBERED (#2406, Organization Detail) ────────
 * "persisted" — a person who folds the rail away folds it once. This browser's
 * preference about this browser's chrome, like the theme beside it
 * (`solum.theme`), so it lives in localStorage under the same prefix and is
 * read and written in a try: a storage that refuses (a private window, a
 * policy) costs the memory and never the rail. It is only ever read back as
 * the exact value this file wrote. */
export const RAIL_COLLAPSED_KEY = 'solum.rail.collapsed'
function readRailCollapsed(): boolean {
  try { return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1' } catch { return false }
}

export function AppShell() {
  const skipToContent = useMessage('a11y.skip_to_content', 'Skip to main content')
  const [cmdOpen, setCmdOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)   // the LEFT nav as an off-canvas drawer on mobile
  /* ONE navigation editing mode, entered by the action that NAMES navigation
   * (#511, #1991, #2267).
   *
   * 50100: *"Invoking Personalize navigation from the user menu or the
   * contextual Do command puts the existing, visible navbar into an editable
   * mode in place. Do not navigate to a second configuration screen."* So this
   * no longer mounts a sheet — it swaps what the SIDEBAR draws. The `<aside>`,
   * its position and its scroll are the same element either way.
   *
   * Table and form customization have their own actions inside the table and
   * form. They used to intercept this global account action by route context,
   * so "Customize experience" opened a different editor on otherwise similar
   * pages. A global action with a route-dependent noun is not discoverable.
   * Navigation now always means navigation, and focus returns to the account
   * trigger because the launching menu item has already unmounted.
   */
  const [navEditor, setNavEditor] = useState(false)
  /* On a phone the bar IS the drawer, and the drawer is shut. Opening the mode
     without opening it would make "Customize navigation" a menu item that does
     nothing you can see (50100 row 7). */
  const openNavEditor = () => {
    setNavEditor(true)
    setNavOpen(true)
  }
  const closeNavEditor = () => {
    setNavEditor(false)
    accountTrigger.current?.focus()
  }
  const accountTrigger = useRef<HTMLButtonElement>(null)
  /* The rail cap's slot — the element the header's ONE account menu draws its
     second trigger into (#2406). State rather than a ref, so the menu re-renders
     its portal when the slot mounts, moves between the column and the drawer,
     or goes. */
  const [userSlot, setUserSlot] = useState<HTMLElement | null>(null)
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed)
  const toggleRail = () => setRailCollapsed((was) => {
    const next = !was
    try { window.localStorage.setItem(RAIL_COLLAPSED_KEY, next ? '1' : '0') } catch { /* memory only */ }
    return next
  })
  const pathname = useRouterState({ select: (st) => st.location.pathname })
  const paletteScope = usePaletteScope()

  /* slm-elevate: the SHELL binds the drawer from the URL (#329/#330).
   *
   * Detail pages used to each call setDrawerContext. With 73 surfaces exactly
   * one remembered, so the sidepane was global chrome with nothing behind it —
   * a contract repeated in every new file is one forgotten in most of them.
   * Deriving it here means a new detail route joins by adding a line to
   * ROUTE_BINDINGS, not by remembering an import.
   *
   * Limit, stated where it is enforced: this only works where the record IS the
   * URL. A surface that picks its record in-page must still bind itself. */
  const matches = useRouterState({ select: (st) => st.matches })
  useEffect(() => {
    const binding = bindingForRoute(matches)
    setDrawerContextFromRoute(binding)
    /* Same binding, second consumer (#512): the support extension reads
       `<meta name="solum-context">` and nothing has ever written one, so every
       capture arrived without a module and triage guessed. Derived from the
       route that already decided what the drawer is bound to, rather than
       computed again — two derivations of "what is on screen" would disagree,
       and the one nobody looks at would be the wrong one. */
    declareContext(binding
      ? { app: 'console', module: binding.entity.split('_')[0],
          entity: binding.entity, record_id: binding.id }
      : { app: 'console' })
  }, [matches])
  /* Close the mobile nav on navigation — UNLESS the bar is being edited. The
     drawer is the edit surface on a phone (50100 row 7) and unmounting it drops
     the working order, which is precisely the silent disappearance row 3
     forbids. Editing is entered deliberately and left deliberately. */
  useEffect(() => { if (!navEditor) setNavOpen(false) }, [pathname, navEditor])
  const navigate = useNavigate()
  const areas = useMyAreas()
  // customer-only = has areas resolved AND backstage is not among them
  const customerOnly = !!areas.data && !areas.data.includes('backstage')
  useEffect(() => {
    if (customerOnly && !customerAllowed(pathname)) void navigate({ to: '/my-org' })
  }, [customerOnly, pathname, navigate])
  const { client, isLive } = useSolum()
  const { session } = useAuth()
  /* ── AN ACCOUNT SWITCH DOES NOT INHERIT A PERSONA (#2328 §2, ruling §7) ────
   *
   * `persona.ts` reads its stored selection once, at module scope, and an
   * in-tab account switch re-imports nothing. The stamp beside the value is
   * what decides — this only asks the store to re-read it for whoever is signed
   * in NOW, which is the moment the previous actor's selection stops being
   * theirs to keep. The pins, the default and the recents need no equivalent:
   * they are keyed by user at the door. */
  useEffect(() => { resetPersonaForPrincipal() }, [session?.user.id])
  // LIVE mode: reconcile the module registry to the real `core.module` ledger.
  useEffect(() => {
    if (!isLive) return
    client.raw().select<{ name: string }>('core', 'module', { columns: 'name' })
      .then((rows) => registry.hydrate(rows.map((r) => r.name)))
      .catch(() => { /* keep defaults */ })
  }, [client, isLive])
  // ── mobile drawer (UX-06 → #420) ──────────────────────────────────────────
  //
  // The drawer used to carry a hand-rolled Escape handler, keydown focus trap,
  // focus restore and aria-modal — the bespoke-backdrop shape #387/UX-02
  // measured as "for a keyboard user it did not open at all" and
  // CommsCalendarPage already migrated off. It is now the shared Modal
  // (variant="sheet"): trap, Escape, aria-hidden on the page behind, and focus
  // restoration all come from the primitive; the classes are unchanged, so the
  // look is the same.
  //
  // The same element is a STATIC SIDEBAR on desktop, and announcing that as a
  // dialog would trap desktop users in the navigation — so the Modal renders
  // only while the viewport is mobile AND the drawer is open; otherwise the
  // plain <aside> stays. The breakpoint is REACTIVE (matchMedia listener), so
  // a rotation or resize while open hands the element back to the static
  // shape instead of leaving a stale dialog around it. One constant shared
  // with the stylesheet, checked by breakpoints.test.ts (UX-03).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches)
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  /* THE STUDIO MAKES THE SHELL INERT (#2328 5, 11's focus arm).
   *
   * 5: the background "is inert and removed from the accessibility tree while
   * open". It is applied HERE rather than inside the Studio because the header
   * and the navigation rail are the shell's, and a component cannot make its
   * own ancestors inert.
   *
   * Read off the PATHNAME, not a piece of state: the Studio is a route, so "is
   * it open" is a question the router already answers, and a second flag could
   * be true while no Studio was mounted. `<main>` is deliberately NOT inert --
   * it is where the Studio itself renders, and an overlay that hid itself would
   * be a very quiet bug. */
  const studioOpen = isStudioPath(pathname)

  /* The rail is only ever COLLAPSED where it is a column: editing needs its
     full width (six controls a row), and on a phone it is a drawer that is
     either shut or open. The preference survives both — it is what the column
     returns to — and neither overwrites it. */
  const collapsed = railCollapsed && !navEditor && !isMobile

  /* ── ⌘K BELONGS TO THE TOPMOST ROUTED WORKSPACE (#2328 §7) ───────────────
   *
   * §7: *"The topmost active routed workspace owns `Ctrl/Cmd+K`. Opening
   * Settings Studio swaps command providers atomically; closing restores the
   * application provider."*
   *
   * The shell declines the key whenever it is not the owner, and the Studio
   * takes it in the same condition — `paletteOwnerFor` is the single arbiter, so
   * the two listeners cannot both answer and cannot both decline. Derived from
   * the pathname rather than from a registration, which is what makes the swap
   * atomic: two `window` keydown listeners resolve by REGISTRATION ORDER, so an
   * owner pushed on mount would win or lose by mount timing.
   *
   * It also closes the shell's palette on the way in. A palette left open behind
   * the overlay would be focusable under an `inert` shell in some browsers and,
   * worse, would still be showing business records while the reader believes
   * they are in Settings. */
  const paletteOwner = paletteOwnerFor(pathname)
  useEffect(() => { if (paletteOwner !== 'application') setCmdOpen(false) }, [paletteOwner])

  useEffect(() => {
    /* Re-registered when ownership changes rather than reading
       `window.location` inside the handler: a second reading of the address
       would be the fork `palette/owner.ts` exists to prevent, and a handler
       closed over a stale owner is the race it exists to prevent. */
    const owned = paletteOwner === 'application'
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (!owned) return
        e.preventDefault()
        setCmdOpen((v) => !v)
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        if (!owned) return
        e.preventDefault()
        launchCopilot({ contextSnapshot: ctxFor(paletteScope, pathname) })
      }
    }
    window.addEventListener('keydown', onKey)
    /* The per-form / per-subgrid ⌘ buttons open the palette with their scope —
       and they are declined under the Studio for the same reason the key is: a
       business palette drawn over the settings overlay is the wrong one. */
    const offOpen = onPaletteOpen(() => { if (owned) setCmdOpen(true) })
    return () => { window.removeEventListener('keydown', onKey); offOpen() }
  }, [paletteOwner, paletteScope, pathname])

  return (
    <div className={s.shell} data-testid="app-shell" data-nav-editing={navEditor ? '' : undefined}
         data-rail={collapsed ? 'collapsed' : undefined}>
      {/* Skip link (UX-05). The nav is ~37 links; without this a keyboard or
          screen-reader user tabs through every one of them on every page load
          before reaching the content. Visually hidden until focused, which is
          the whole point — it must be reachable, not decorative. */}
      <a href="#main" className={s.skipLink}>{skipToContent}</a>
      {/* ONE account menu, placed by viewport (#1991, ruling 72563 §7): the
          navigation footer on desktop, the top bar on a compact viewport where
          the column is a closed drawer and signing out must not require opening
          it first. Two mounted menus would be the fork packages/ui exists to
          prevent, and account-switcher.spec.ts drives the compact one at 390x844. */}
      <Topbar onOpenCommand={() => setCmdOpen(true)} onToggleNav={() => setNavOpen((v) => !v)} navOpen={navOpen}
        accountTrigger={accountTrigger} onPersonalizeNav={openNavEditor} inert={studioOpen}
        onConnectedApps={() => void navigate({ to: CONNECTED_APPS_PATH })}
        accountCapSlot={userSlot}
        />
      <div className={s.body} data-nav-editing={navEditor ? '' : undefined}>
        {/* ONE composed column, around BOTH modes (#1991, ruling 72563).
            The identity header and the signed-in user footer are outside the
            scroller and outside the mode swap, so the editor preserves the
            read composition instead of replacing it — and the account trigger
            focus returns to stays mounted for the whole of edit mode. */}
        {isMobile && navOpen
          ? <MobileNavDrawer editing={Boolean(navEditor)} onClose={() => setNavOpen(false)}>
              <ConsoleNavigationShell accountTrigger={accountTrigger}
                                      onPersonalizeNav={openNavEditor}>
                {navEditor ? <NavEditMode onClose={closeNavEditor} /> : <Nav />}
              </ConsoleNavigationShell>
            </MobileNavDrawer>
          : <aside className={cx(s.side, navEditor && s.sideEditing)} data-testid="nav-drawer"
                   inert={studioOpen}>
              {/* The COLUMN — and only the column — collapses and carries the
                  user cap (#2406 leading designs). The drawer above gets
                  neither: see `ConsoleNavigationShell`'s `userSlot`. */}
              <ConsoleNavigationShell accountTrigger={accountTrigger}
                                      onPersonalizeNav={openNavEditor}
                                      collapse={isMobile || navEditor ? undefined
                                        : { collapsed, onToggle: toggleRail }}
                                      userSlot={isMobile ? undefined : setUserSlot}>
                {navEditor ? <NavEditMode onClose={closeNavEditor} /> : <Nav collapsed={collapsed} />}
              </ConsoleNavigationShell>
            </aside>}
        {/* tabIndex=-1 so the skip link can actually move focus here: an anchor
            to a non-focusable element scrolls but leaves focus behind, so the
            next Tab returns to the nav and the link achieves nothing. */}
        {/* THE SCROLLER IS RESTORED BY THE ROUTER, AND NOT BY AN ATTRIBUTE
            HERE. #2328 5 asks that closing the Settings Studio return to *"the
            exact prior route AND scroll position"*, and `#main` is this shell's
            only scroller, so it is the element that has to come back.

            A `data-scroll-restoration-id="main"` stood on this line for exactly
            as long as it took to mutation-test it: removing it left
            `e2e-browser/settings-studio.spec.ts` GREEN, while removing
            `scrollRestoration: true` from `createRouter` reddened it by name.
            TanStack caches an element by a generated selector when it carries
            no id, so the attribute changed nothing and was deleted rather than
            kept as reassurance -- an inert declaration is worse than none,
            because the next reader takes it for the mechanism.

            The earlier failure that prompted it was the TEST's own: it opened
            the Studio with a hard `page.goto`, which the router never sees. */}
        <main id="main" tabIndex={-1} className={s.main}><Outlet /></main>
        <ShellDrawer />
      </div>
      {/* ── THE FOOTER IS GONE (#2328 §1, ROOT ruling 74071 §3) ──────────────
          `<ShellFooter><ContextDock /></ShellFooter>` stood here: a reserved
          full-width row carrying the acting persona and the Marble attribution.
          Both responsibilities have an explicit destination now — the persona is
          in the header account menu, the attribution is the floating mount below
          — so the row itself goes, and `.body { flex: 1 }` reclaims its height
          with no second scroller, because the footer was a flex ITEM and never a
          padding reserve. §11's first arm is `AppShell.chrome.test.ts`.

          The attribution obligation lives in the launcher contract — the Marble
          mark, the `OnMarble` wordmark and the registered `by Moonbase` mark
          are all inside one control. #2406 §5.3 moved that control OUT of this
          corner and into the top bar, so it is mounted by `Topbar` now and the
          canvas corner carries nothing at all. B2's `ShellAttribution`
          placeholder stays retired. */}
      {/* Ruling §10's routed half. `beforeunload` is the registry's own and is
          installed by the store; this is the in-app navigation blocker, which
          `beforeunload` cannot see at all. */}
      <UnsavedChangesGuard />
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
      <ActivityComposerHost />
      {/* #524: fills the CRM provider caches from core.setting, once. */}
      <CrmSettingsSync client={client} isLive={isLive} />
      {/* #2325: the one owner of the governed recents row — reads `my_settings`
          (shared with `usePins`, so the pair is one round trip), hydrates the
          module cache ⌘K and the relationship picker read synchronously, and
          flushes visits back. A component rather than a hook call in `AppShell`
          itself so a settings refetch re-renders this and not the whole shell. */}
      <RecentsStore />
    </div>
  )
}

/** Mounts the governed recents store. Renders nothing; see `recents.ts`. */
function RecentsStore() {
  useRecentsStore()
  return null
}

/**
 * The off-canvas nav as a Modal sheet. The slide-in used to ride a class
 * toggle (`.side` → `.sideOpen`) on an always-mounted aside; the Modal mounts
 * on open, so the panel starts off-canvas (`.side` alone) and picks up
 * `.sideOpen` a frame later — same classes, same 200ms transform, now on a
 * surface a keyboard can actually operate.
 */
function MobileNavDrawer({ onClose, editing, children }: {
  onClose: () => void
  /** Widens the sheet and hands it the flex column the dock sits at the end of. */
  editing: boolean
  children: React.ReactNode
}) {
  const [slid, setSlid] = useState(false)
  useEffect(() => {
    // Double rAF: the first frame must PAINT with the off-canvas transform,
    // or the transition has nothing to run from and the drawer just appears.
    let id2 = 0
    const id1 = requestAnimationFrame(() => { id2 = requestAnimationFrame(() => setSlid(true)) })
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
  }, [])
  return (
    <Modal label="Navigation" variant="sheet" testid="nav-drawer"
      overlayClassName={s.backdrop}
      /* While the bar is being EDITED this sheet does not dismiss itself. Escape
         and a backdrop tap both close the drawer, and the drawer is the edit
         surface on a phone — closing it would discard the working order with
         nothing said (50100 row 3). `NavEditMode` owns Escape instead, and the
         way out is Cancel or Save. */
      dismissable={!editing}
      className={cx(s.side, slid && s.sideOpen, editing && s.sideEditing)} onClose={onClose}>
      {children}
    </Modal>
  )
}
